import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Modal,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Loader } from "../../components";
import StepProgressBar from "../../components/StepProgressBar";
import { createSurveyAttempt } from "../../ducks/actions";

const useStyles = makeStyles(theme => ({
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    minHeight: "50%",
    maxHeight: "80%",
    backgroundColor: theme.palette.primary.dark,
    border: "0px solid transparent",
    outline: "none",
    overflowY: "auto"
  },
  gridContainer: {
    gap: "1rem"
  },
  stepBarCell: {
    marginBottom: "5rem"
  },
  whiteText: {
    color: "#FFFFFF"
  },
  answersCell: {
    marginTop: "1rem"
  },
  answersButtonsGroup: {
    gap: "1rem",
    "& label": {
      background: "#FFFFFF",
      borderRadius: "2rem",
      marginLeft: "1rem",
      padding: "0.25rem"
    }
  },
  actionsCell: {
    gap: "1rem",
    marginTop: "2rem",
    marginBottom: "4rem"
  },
  gridContainerSurveyCompleted: {
    gap: "1rem",
    textAlign: "center"
  },
  closeIcon: {
    width: "2rem",
    height: "2rem"
  },
  surveyButton: {
    margin: "0 20px"
  }
}));

export default function SurveyModal({ survey, isOpen, handleClose }) {
  const classes = useStyles();
  const intl = useIntl();

  const dispatch = useDispatch();

  const [activeQuestion, setActiveQuestion] = useState(0);

  const [answersIds, setAnswersIds] = useState([]);

  const [surveyAttemptResult, setSurveyAttemptResult] = useState(null);

  const [isSurveyAttemptLoading, setIsSurveyAttemptLoading] = useState(false);

  const onAnswerChange = evt => {
    setAnswersIds(() => {
      if (answersIds[activeQuestion]) {
        return answersIds.map((answer, questionIndex) =>
          questionIndex === activeQuestion ? evt.target.value : answer
        );
      }
      return [
        ...answersIds.slice(0, activeQuestion),
        evt.target.value,
        ...answersIds.slice(activeQuestion)
      ];
    });
  };

  const sendAnswers = async () => {
    setIsSurveyAttemptLoading(true);

    const {
      data: { survey_attempt: surveyAttempt }
    } = await dispatch(
      createSurveyAttempt({
        surveyId: survey?.id,
        givenAnswers: answersIds.map((answer, index) => ({
          question_id: survey?.questions[index]?.id,
          given_answer_id: answer
        }))
      })
    );

    setSurveyAttemptResult(surveyAttempt);
    setIsSurveyAttemptLoading(false);
  };

  return (
    <>
      <Modal disableBackdropClick onClose={handleClose} open={isOpen}>
        <Box
          display={surveyAttemptResult || isSurveyAttemptLoading ? "flex" : "block"}
          alignItems="center"
          justifyContent="center"
          className={classes.modalBox}
        >
          {surveyAttemptResult || isSurveyAttemptLoading ? (
            <Loader loading={isSurveyAttemptLoading} circular>
              <Grid container className={classes.gridContainerSurveyCompleted}>
                <Grid container item justify="center" xs={12}>
                  {surveyAttemptResult?.is_passed ? (
                    <Typography className={classes.whiteText} variant="h4">{`${intl.formatMessage({
                      id: "Pages.Course.Survey.CompletedSuccessfully",
                      defaultMessage: "You passed the survey successfully!"
                    })}`}</Typography>
                  ) : (
                    <Typography className={classes.whiteText} variant="h4">{`${intl.formatMessage({
                      id: "Pages.Course.Survey.CompletedFailing",
                      defaultMessage: "You didn't pass the survey!"
                    })}`}</Typography>
                  )}
                </Grid>
                <Grid container item justify="center" xs={12}>
                  <Typography className={classes.whiteText} variant="body2">{`${intl.formatMessage({
                    id: "Pages.Course.Survey.RespondedCorrectly",
                    defaultMessage: "You responded correctly to"
                  })} ${surveyAttemptResult?.correct_answer_percentage}% ${intl.formatMessage({
                    id: "Pages.Course.Survey.OfQuestions",
                    defaultMessage: "of questions"
                  })}`}</Typography>
                </Grid>
                <Grid container item justify="center" xs={12}>
                  {surveyAttemptResult?.is_passed && (
                    <Button
                      className={classes.surveyButton}
                      color="secondary"
                      variant="contained"
                      download
                      target="_blank"
                      href={surveyAttemptResult.certificate.pdf.url}
                    >
                      {intl.formatMessage({
                        id: "Pages.Course.Survey.DownloadCertificate",
                        defaultMessage: "DOWNLOAD CERTIFICATE"
                      })}
                    </Button>
                  )}
                  <Button
                    className={classes.surveyButton}
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      handleClose();
                      setActiveQuestion(0);
                      setAnswersIds([]);
                      setSurveyAttemptResult(null);
                    }}
                  >
                    {intl.formatMessage({
                      id: "Pages.Course.Survey.BackToCourse",
                      defaultMessage: "GO BACK TO COURSE"
                    })}
                  </Button>
                </Grid>
              </Grid>
            </Loader>
          ) : (
            <Grid container className={classes.gridContainer}>
              <Grid container item justify="flex-end" className={classes.whiteText} xs={12}>
                <IconButton size="medium" onClick={handleClose}>
                  <CloseIcon className={classes.closeIcon} />
                </IconButton>
              </Grid>
              <Grid container item xs={12} className={classes.stepBarCell}>
                <StepProgressBar
                  activeStep={activeQuestion + 1}
                  stepNumber={survey?.questions?.length}
                  type="survey"
                />
              </Grid>
              <Grid container item justify="center" xs={12}>
                <Typography className={classes.whiteText} variant="body2">{`${intl.formatMessage({
                  id: "Pages.Course.Survey.Question",
                  defaultMessage: "QUESTION"
                })} ${activeQuestion + 1}/${survey?.questions?.length}`}</Typography>
              </Grid>
              <Grid container item justify="center" xs={12}>
                <Typography align="center" className={classes.whiteText} variant="h4">
                  {survey?.questions[activeQuestion]?.body}
                </Typography>
              </Grid>
              <Grid className={classes.answersCell} container item justify="center" xs={12}>
                <FormControl className={classes.answersControl}>
                  <RadioGroup
                    className={classes.answersButtonsGroup}
                    aria-labelledby="answers-buttons-group"
                    name="answers-buttons-group"
                    value={!answersIds[activeQuestion] ? null : answersIds[activeQuestion]}
                    onChange={onAnswerChange}
                  >
                    {survey?.questions[activeQuestion]?.answers?.map(answer => (
                      <FormControlLabel
                        key={answer.id}
                        value={answer.id}
                        control={<Radio />}
                        label={answer.body}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid container item className={classes.actionsCell} justify="center" xs={12}>
                <Button
                  color="secondary"
                  disabled={activeQuestion === 0}
                  variant="contained"
                  onClick={() => {
                    setActiveQuestion(activeQuestion - 1);
                  }}
                >
                  {intl.formatMessage({
                    id: "Pages.Course.Survey.PreviousQuestion",
                    defaultMessage: "PREVIOUS QUESTION"
                  })}
                </Button>
                {activeQuestion === survey?.questions?.length - 1 ? (
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={!answersIds[activeQuestion]}
                    onClick={sendAnswers}
                  >
                    {intl.formatMessage({
                      id: "Pages.Course.Survey.SendAnswers",
                      defaultMessage: "SEND ANSWERS"
                    })}
                  </Button>
                ) : (
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={!answersIds[activeQuestion]}
                    onClick={() => {
                      setActiveQuestion(activeQuestion + 1);
                    }}
                  >
                    {intl.formatMessage({
                      id: "Pages.Course.Survey.NextQuestion",
                      defaultMessage: "NEXT QUESTION"
                    })}
                  </Button>
                )}
              </Grid>
            </Grid>
          )}
        </Box>
      </Modal>
    </>
  );
}
